/**
 * homeSuggestionEngine.js — Pure, testable decision engine for Home suggestions
 * 
 * Single source of truth for the Home nudge logic.
 * No side effects, no localStorage reads, no React dependencies.
 * 
 * @param {Object} userContext - Real user data
 * @param {Object|null} simulationContext - Override context (null = use real)
 * @returns {Object} Full suggestion state with debug info
 */

// ─── Text pools ───
const SUBTITLES = {
  matin:   { fr: 'Commencer en douceur',       en: 'Start gently' },
  journee: { fr: 'Faites une pause',            en: 'Take a break' },
  soir:    { fr: 'Ralentir progressivement',    en: 'Slowly wind down' },
  nuit:    { fr: 'Se laisser aller',             en: 'Let go' },
};

const TIME_CARD_TEXT = {
  matin:   { fr: 'Un son plus clair pour bien démarrer',     en: 'A brighter sound to start your day' },
  journee: { fr: 'Un son plus équilibré', en: 'More balanced sound' },
  soir:    { fr: 'Un son plus doux pour se détendre',        en: 'A softer sound to unwind' },
  nuit:    { fr: 'Un son plus apaisant pour la nuit',        en: 'A soothing sound for the night' },
};

const THEME_CARD_TEXT = {
  nature:     { fr: 'Encore plus naturel',  en: 'Even more natural' },
  relaxation: { fr: 'Encore plus fluide',   en: 'Even smoother' },
  sleep:      { fr: 'Encore plus doux',     en: 'Even softer' },
  meditation: { fr: 'Encore plus profond',  en: 'Even deeper' },
};

const VARIANT_POOL = {
  fr: ['Encore plus adapté', 'Encore plus naturel', 'Encore plus fluide', 'Encore plus doux'],
  en: ['Even more tailored', 'Even more natural', 'Even smoother', 'Even softer'],
};

const TIME_GLOW = {
  matin:   { opacity: 0.10, hue: 'rgba(139,92,246,0.08)',  tint: 'lighter' },
  journee: { opacity: 0.12, hue: 'rgba(99,102,241,0.10)',  tint: 'neutral' },
  soir:    { opacity: 0.08, hue: 'rgba(79,70,229,0.08)',   tint: 'warm' },
  nuit:    { opacity: 0.06, hue: 'rgba(55,48,163,0.06)',   tint: 'darker' },
};

const TIME_ANIMATION = {
  matin:   { speed: 1.0,  pulse: '3s' },
  journee: { speed: 1.1,  pulse: '2.8s' },
  soir:    { speed: 0.8,  pulse: '3.5s' },
  nuit:    { speed: 0.6,  pulse: '4.5s' },
};

// ─── Stage mapping for J-notation ───
const resolveStage = (daysSince, sessionCount, personalizationDone) => {
  if (personalizationDone) return 'COMPLETED';
  if (daysSince < 1) return 'J1';
  if (daysSince < 3 || sessionCount <= 1) return 'J2';
  if (daysSince < 5 || sessionCount < 5) return 'J3';
  return 'J5';
};

/**
 * Main decision function — pure, no side effects
 * 
 * @param {Object} userContext
 *   - daysSinceInstall {number}
 *   - sessionCount {number}
 *   - lastThemeUsed {string|null}
 *   - hasCompletedPersonalization {boolean}
 *   - timeOfDay {string} 'matin'|'journee'|'soir'|'nuit'
 *   - isFr {boolean}
 *   - variantIndex {number} current rotation index
 * @param {Object|null} simulationContext - same shape as userContext, overrides if provided
 * @returns {Object} suggestion state
 */
export const getHomeSuggestionState = (userContext, simulationContext = null) => {
  const ctx = simulationContext || userContext;
  const {
    daysSinceInstall = 0,
    sessionCount = 0,
    lastThemeUsed = null,
    hasCompletedPersonalization = false,
    timeOfDay = 'journee',
    isFr = true,
    variantIndex = 0,
  } = ctx;

  const lang = isFr ? 'fr' : 'en';
  const stage = resolveStage(daysSinceInstall, sessionCount, hasCompletedPersonalization);
  const subtitle = SUBTITLES[timeOfDay]?.[lang] || SUBTITLES.journee[lang];
  const glow = TIME_GLOW[timeOfDay] || TIME_GLOW.journee;
  const animation = TIME_ANIMATION[timeOfDay] || TIME_ANIMATION.journee;

  let visible = false;
  let title = null;
  let nudgeSubtitle = null;
  let cta = null;
  let variantId = null;
  let accent = 'indigo';
  let completed = false;
  let reason = '';

  if (hasCompletedPersonalization) {
    // Post-personalization
    visible = true;
    title = isFr ? 'Expérience optimisée' : 'Optimized experience';
    nudgeSubtitle = isFr ? 'Hearen s\'adapte à vous automatiquement' : 'Hearen adapts to you automatically';
    cta = null;
    completed = true;
    accent = 'emerald';
    variantId = 'completed';
    reason = 'All personalization tests completed';
  } else if (daysSinceInstall < 1) {
    // J1: No nudge
    visible = false;
    reason = 'Day 0 — pure experience, no suggestion';
    variantId = null;
  } else if (sessionCount <= 1) {
    // J2 CAS 1: New user
    visible = true;
    title = isFr ? 'Hearen peut s\'adapter à vous' : 'Hearen can adapt to you';
    nudgeSubtitle = TIME_CARD_TEXT[timeOfDay]?.[lang] || TIME_CARD_TEXT.journee[lang];
    cta = isFr ? 'Découvrir' : 'Discover';
    accent = 'indigo';
    variantId = 'new_user';
    reason = `New user (sessions=${sessionCount}, days=${daysSinceInstall})`;
  } else if (sessionCount >= 5) {
    // J5 CAS 3: Regular user without personalization
    visible = true;
   title = isFr ? 'Profil presque prêt' : 'Profile almost ready';
    nudgeSubtitle = TIME_CARD_TEXT[timeOfDay]?.[lang] || TIME_CARD_TEXT.journee[lang];
    cta = isFr ? 'Optimiser' : 'Optimize';
    accent = 'purple';
    variantId = 'regular_no_perso';
    reason = `Regular user without personalization (sessions=${sessionCount})`;
  } else if (sessionCount >= 3) {
    // J3 CAS 2: Engaged user — theme-based
    visible = true;
    const themeText = lastThemeUsed && THEME_CARD_TEXT[lastThemeUsed];
    const pool = VARIANT_POOL[lang];
    const safeIdx = variantIndex % pool.length;
    title = themeText ? themeText[lang] : pool[safeIdx];
    nudgeSubtitle = TIME_CARD_TEXT[timeOfDay]?.[lang] || TIME_CARD_TEXT.journee[lang];
    cta = isFr ? 'Essayer' : 'Try it';
    accent = lastThemeUsed === 'nature' ? 'emerald'
           : lastThemeUsed === 'sleep' ? 'blue'
           : lastThemeUsed === 'meditation' ? 'violet'
           : 'purple';
    variantId = themeText ? `theme_${lastThemeUsed}` : `variant_${safeIdx}`;
    reason = `Engaged user (sessions=${sessionCount}, theme=${lastThemeUsed || 'none'})`;
  } else if (daysSinceInstall >= 2) {
    // J2+: Curiosity
    visible = true;
    title = isFr ? 'Une version encore plus adaptée' : 'An even more tailored version';
    nudgeSubtitle = TIME_CARD_TEXT[timeOfDay]?.[lang] || TIME_CARD_TEXT.journee[lang];
    cta = isFr ? 'Découvrir' : 'Discover';
    accent = 'indigo';
    variantId = 'curiosity';
    reason = `Days since install >= 2, low sessions (days=${daysSinceInstall}, sessions=${sessionCount})`;
  } else {
    reason = `No matching rule (days=${daysSinceInstall}, sessions=${sessionCount})`;
  }

  return {
    visible,
    stage,
    variantId,
    title,
    subtitle: nudgeSubtitle,
    cta,
    completed,
    accent,
    reason,
    homeSubtitle: subtitle,
    glow,
    animation,
    debug: {
      daysSinceInstall,
      sessionCount,
      lastThemeUsed,
      hasCompletedPersonalization,
      timeOfDay,
    },
  };
};

export default getHomeSuggestionState;
